/**
 * Pickup eligibility helpers for the collection-page Same Day Pickup filter.
 *
 * Leaf module: only reads the products grid in the DOM plus plain Stockist store
 * objects, with no other imports — so it can be safely imported by both
 * StoreSelector and PickupStoreFilter without creating an import cycle with
 * availability-filters / pickup-store-filter-app.
 */

function getShopifyStoreId(store) {
  const field = store?.custom_fields?.find((f) => f.name === "shopify_store_id");
  return field?.value || null;
}

/**
 * Shopify location ids for which at least one currently-loaded product would
 * remain visible under the pickup store filter — i.e. a fulfillable product with
 * available stock at that location. Mirrors filterProductsByStore's predicate so
 * the picker can never disagree with the product grid.
 *
 * @returns {Set<string>|null} null when there is no products grid (non-collection
 *   context) so callers can treat "unknown" as "do not filter".
 */
export function getCollectionPickupLocationIds() {
  const grid = document.getElementById("productsGrid");
  if (!grid) {
    return null;
  }

  const ids = new Set();
  grid.querySelectorAll(".product-item__wrapper").forEach((wrapper) => {
    const item = wrapper.querySelector("[data-in-store-fulfillable]") || wrapper;
    if (item.dataset.inStoreFulfillable !== "true") return;
    if (item.dataset.hasStoreAvailability !== "true") return;
    try {
      JSON.parse(item.dataset.storeAvailabilities).forEach((variant) => {
        variant.availabilities.forEach((availability) => {
          if (availability.available) {
            ids.add(String(availability.locationId));
          }
        });
      });
    } catch (err) {
      // Malformed availability data — skip rather than poison the eligible set.
    }
  });
  return ids;
}

/**
 * Whether a Stockist store has pickup-eligible products in the current collection.
 * When eligibleIds is null (unknown / no grid), returns true so we never
 * over-filter the shared picker in non-pickup contexts.
 *
 * @param {object} store Stockist store object with custom_fields
 * @param {Set<string>|null} eligibleIds result of getCollectionPickupLocationIds
 * @returns {boolean}
 */
export function isStoreEligibleForPickup(store, eligibleIds) {
  if (!eligibleIds) {
    return true;
  }
  const shopifyId = getShopifyStoreId(store);
  return shopifyId ? eligibleIds.has(String(shopifyId)) : false;
}
